import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';

/* Collapsible XML tree view that syncs with the left-side form collapse state. */

const OpenTag = ({ el }) => (
  <>
    <span className="text-slate-500">&lt;</span>
    <span className="text-emerald-400">{el.tagName}</span>
    {Array.from(el.attributes).map((a) => (
      <span key={a.name}>
        {' '}
        <span className="text-sky-400">{a.name}</span>
        <span className="text-slate-500">=</span>
        <span className="text-amber-300">"{a.value}"</span>
      </span>
    ))}
  </>
);

const CloseTag = ({ el }) => (
  <>
    <span className="text-slate-500">&lt;/</span>
    <span className="text-emerald-400">{el.tagName}</span>
    <span className="text-slate-500">&gt;</span>
  </>
);

export default function XmlTreeView({ xml, schemaType, lpKeys = [], drillKeys = [], syncCollapsed = {}, activeKey = null, showLineNumbers = false, jump = null }) {
  const { header, root } = useMemo(() => {
    if (!xml) return { header: '', root: null };
    // Split off XML declaration / DOCTYPE, find the root element start
    let rootStart = -1;
    for (let i = 0; i < xml.length; i++) {
      if (xml[i] === '<' && xml[i + 1] !== '?' && xml[i + 1] !== '!') { rootStart = i; break; }
    }
    const head = rootStart > 0 ? xml.slice(0, rootStart).trim() : '';
    const body = rootStart >= 0 ? xml.slice(rootStart) : xml;
    try {
      const doc = new DOMParser().parseFromString(body, 'application/xml');
      if (doc.getElementsByTagName('parsererror').length) return { header: head, root: null };
      return { header: head, root: doc.documentElement };
    } catch {
      return { header: head, root: null };
    }
  }, [xml]);

  // Map node paths -> section keys (levelledPara/proceduralStep, crewDrill, prelim/close)
  const keyByPath = useMemo(() => {
    const map = new Map();
    if (!root) return map;
    const lpTag = schemaType === 'procedural' ? 'proceduralStep' : 'levelledPara';
    let lpIdx = 0;
    let drillIdx = 0;
    const walk = (el, path) => {
      const name = el.tagName;
      const parentName = el.parentElement ? el.parentElement.tagName : '';
      if (name === lpTag && parentName !== lpTag) {
        if (lpKeys[lpIdx]) map.set(path, lpKeys[lpIdx]);
        lpIdx += 1;
      } else if (name === 'crewDrill') {
        if (drillKeys[drillIdx]) map.set(path, drillKeys[drillIdx]);
        drillIdx += 1;
      } else if (name === 'preliminaryRqmts') {
        map.set(path, 'prelim');
      } else if (name === 'closeRqmts') {
        map.set(path, 'close');
      }
      Array.from(el.children).forEach((c, i) => walk(c, `${path}/${i}`));
    };
    walk(root, '0');
    return map;
  }, [root, schemaType, lpKeys, drillKeys]);

  const [toggles, setToggles] = useState({});

  // When the left-side collapse state changes, it wins over manual preview toggles
  useEffect(() => {
    setToggles((t) => {
      const next = { ...t };
      for (const p of keyByPath.keys()) delete next[p];
      return next;
    });
  }, [syncCollapsed, keyByPath]);

  const isCollapsed = useCallback((path) => {
    if (toggles[path] !== undefined) return toggles[path];
    const key = keyByPath.get(path);
    return key ? !!syncCollapsed[key] : false;
  }, [toggles, keyByPath, syncCollapsed]);

  const toggle = (path) => setToggles((t) => ({ ...t, [path]: !isCollapsed(path) }));

  // ── Scroll sync: bring the section being edited on the left into view ──
  const activeRef = React.useRef(null);
  const activePath = useMemo(() => {
    if (!activeKey) return null;
    for (const [p, k] of keyByPath.entries()) {
      if (k === activeKey) return p;
    }
    return null;
  }, [keyByPath, activeKey]);

  useEffect(() => {
    const el = activeRef.current;
    if (!el) return;
    const scroller = el.closest('.overflow-auto');
    if (!scroller) return;
    const eTop = el.getBoundingClientRect().top - scroller.getBoundingClientRect().top + scroller.scrollTop;
    scroller.scrollTo({ top: Math.max(eTop - 48, 0), behavior: 'smooth' });
  }, [activePath, root]);

  // ── Error jump: locate the element from a schema-error XPath, expand ancestors, scroll + flash ──
  const [flashPath, setFlashPath] = useState(null);
  const flashRef = React.useRef(null);
  useEffect(() => {
    if (!jump || !jump.path || !root) return;
    const doc = root.ownerDocument;
    let target = null;
    try {
      target = doc.evaluate(jump.path, doc, null, XPathResult.FIRST_ORDERED_NODE_TYPE, null).singleNodeValue;
    } catch { /* invalid xpath */ }
    if (!target || target.nodeType !== 1) return;
    const chain = [];
    let cur = target;
    while (cur && cur.nodeType === 1) { chain.unshift(cur); cur = cur.parentElement; }
    let p = '0';
    const ancestors = [p];
    for (let i = 1; i < chain.length; i++) {
      const idx = Array.from(chain[i - 1].children).indexOf(chain[i]);
      if (idx < 0) return;
      p = `${p}/${idx}`;
      ancestors.push(p);
    }
    setToggles((t) => {
      const next = { ...t };
      ancestors.slice(0, -1).forEach((ap) => { next[ap] = false; });
      return next;
    });
    setFlashPath(p);
  }, [jump, root]);

  useEffect(() => {
    if (!flashPath) return;
    const el = flashRef.current;
    if (el) {
      const scroller = el.closest('.overflow-auto');
      if (scroller) {
        const eTop = el.getBoundingClientRect().top - scroller.getBoundingClientRect().top + scroller.scrollTop;
        scroller.scrollTo({ top: Math.max(eTop - 60, 0), behavior: 'smooth' });
      }
    }
    const t = setTimeout(() => setFlashPath(null), 3000);
    return () => clearTimeout(t);
  }, [flashPath]);

  const lineCounter = { n: 0 };
  const gutter = () => {
    if (!showLineNumbers) return null;
    lineCounter.n += 1;
    return (
      <span className="w-8 shrink-0 text-right pr-2 text-slate-600 select-none border-r border-slate-800 mr-2">
        {lineCounter.n}
      </span>
    );
  };

  const renderNode = (el, path, depth) => {
    const kids = Array.from(el.childNodes).filter(
      (n) => n.nodeType === 1 || (n.nodeType === 3 && n.textContent.trim())
    );
    const elementKids = kids.filter((n) => n.nodeType === 1);
    const pad = { paddingLeft: depth * 14 };

    if (elementKids.length === 0) {
      const text = (el.textContent || '').trim();
      const isFlash = path === flashPath;
      return (
        <div key={path} ref={isFlash ? flashRef : (path === activePath ? activeRef : undefined)} className={`flex ${isFlash ? 'bg-red-500/20 ring-1 ring-red-500/60 rounded' : ''}`} data-testid={isFlash ? 'xml-error-flash' : undefined}>
          {gutter()}
          <div style={pad} className="pl-3.5 min-w-0">
            <OpenTag el={el} />
            {text ? (
              <>
                <span className="text-slate-500">&gt;</span>
                <span className="text-slate-200">{text}</span>
                <CloseTag el={el} />
              </>
            ) : (
              <span className="text-slate-500">/&gt;</span>
            )}
          </div>
        </div>
      );
    }

    const collapsed = isCollapsed(path);
    const isActive = path === activePath;
    const isFlash = path === flashPath;
    return (
      <div key={path} ref={isFlash ? flashRef : (isActive ? activeRef : undefined)} className={isFlash ? 'bg-red-500/20 rounded ring-1 ring-red-500/60' : (isActive ? 'bg-violet-500/10 rounded ring-1 ring-violet-500/30' : undefined)} data-testid={isFlash ? 'xml-error-flash' : undefined}>
        <div
          className="cursor-pointer hover:bg-slate-800/70 rounded flex items-start"
          onClick={() => toggle(path)}
          data-testid={`xml-node-${el.tagName}-${path.replace(/\//g, '-')}`}
        >
          {gutter()}
          <div style={pad} className="flex items-start min-w-0">
            <span className="text-slate-500 shrink-0 mt-[3px]">
              {collapsed ? <ChevronRight className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
            </span>
            <span className="min-w-0">
              <OpenTag el={el} />
              <span className="text-slate-500">&gt;</span>
              {collapsed && (
                <>
                  <span className="text-slate-600"> ⋯ </span>
                  <CloseTag el={el} />
                </>
              )}
            </span>
          </div>
        </div>
        {!collapsed && (
          <>
            {kids.map((n, i) =>
              n.nodeType === 1 ? (
                renderNode(n, `${path}/${elementKids.indexOf(n)}`, depth + 1)
              ) : (
                <div key={`${path}-t${i}`} className="flex">
                  {gutter()}
                  <div style={{ paddingLeft: (depth + 1) * 14 }} className="pl-3.5 text-slate-200">
                    {n.textContent.trim()}
                  </div>
                </div>
              )
            )}
            <div className="flex">
              {gutter()}
              <div style={pad} className="pl-3.5">
                <CloseTag el={el} />
              </div>
            </div>
          </>
        )}
      </div>
    );
  };

  if (!root) {
    if (showLineNumbers && xml) {
      return (
        <div data-testid="xml-tree-view">
          {xml.split('\n').map((ln, i) => (
            <div key={i} className="flex">
              {gutter()}
              <pre className="text-green-400 whitespace-pre-wrap min-w-0">{ln || ' '}</pre>
            </div>
          ))}
        </div>
      );
    }
    return <pre className="text-green-400 whitespace-pre-wrap">{xml}</pre>;
  }

  return (
    <div data-testid="xml-tree-view">
      {header && (showLineNumbers ? (
        header.split('\n').map((ln, i) => (
          <div key={`h-${i}`} className="flex">
            {gutter()}
            <pre className="text-slate-500 whitespace-pre-wrap min-w-0">{ln || ' '}</pre>
          </div>
        ))
      ) : (
        <pre className="text-slate-500 whitespace-pre-wrap mb-1">{header}</pre>
      ))}
      {renderNode(root, '0', 0)}
    </div>
  );
}
